import { Flame, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface HomeHeroConsistencyCardProps {
  consistency: number;
  tasksDone: number;
  totalTasks: number;
  streak: number;
  comparisonText: string;
}

const HomeHeroConsistencyCard = ({
  consistency,
  tasksDone,
  totalTasks,
  streak,
  comparisonText,
}: HomeHeroConsistencyCardProps) => {
  const getComparisonIcon = () => {
    if (comparisonText.includes('↑')) return <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />;
    if (comparisonText.includes('↓')) return <TrendingDown className="h-3.5 w-3.5 text-rose-400" />;
    if (comparisonText.includes('→')) return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
    return null;
  };

  const getSupportiveCopy = () => {
    if (consistency === 100) return '¡Día perfecto! Todas tus acciones completadas.';
    if (consistency >= 66) return '¡Casi! Un paso más para completar tu día.';
    if (consistency > 0) return 'Buen comienzo. Cada acción cuenta.';
    return 'Empieza con tu primera acción del día.';
  };

  return (
    <div className="glass-card bg-card rounded-2xl p-5 shadow-card relative overflow-hidden">
      {/* Decorative gradient blob */}
      <div className="absolute -top-8 -right-8 w-28 h-28 bg-primary/8 rounded-full blur-2xl pointer-events-none" />

      <div className="relative z-10">
        {/* Top row */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Consistencia del día
          </span>
          <span className="text-xs text-muted-foreground">Hoy</span>
        </div>

        {/* Main score */}
        <div className="flex items-end gap-3 mb-3">
          <span className="text-5xl font-heading font-bold text-primary leading-none">
            {consistency}%
          </span>
          <div className="pb-1.5 space-y-0.5">
            <span className="text-sm text-foreground font-medium block">
              {tasksDone} de {totalTasks} acciones clave
            </span>
            {streak > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Flame className="h-3.5 w-3.5 text-wellness-peach" />
                Racha: {streak} {streak === 1 ? 'día' : 'días'}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2.5 bg-muted rounded-full overflow-hidden mb-3">
          <div
            className="h-full gradient-primary rounded-full transition-all duration-700 ease-out"
            style={{ width: `${consistency}%` }}
          />
        </div>

        {/* Bottom row */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{getSupportiveCopy()}</p>
          {comparisonText && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {getComparisonIcon()}
              {comparisonText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeHeroConsistencyCard;
